import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DateField from "@/components/DateField";
import SalesCalculator from "@/pages/admin/SalesCalculator";
import { refreshAdminNotifications } from "@/components/NotificationBell";
import { AdminPageHeader } from "@/pages/admin/AdminUx";
import "./SalesCalculatorV2.css";

const EMPTY_ANSWER = {
  status: "empty",
  unknown: false,
  value: "",
  values: [],
  text: "",
  date: "",
  custom_request: "",
};

function evalShowWhen(rule, answers) {
  if (!rule) return true;
  if (rule.all) return rule.all.every((part) => evalShowWhen(part, answers));
  if (rule.any) return rule.any.some((part) => evalShowWhen(part, answers));
  if (rule.answered) {
    const row = answers[rule.answered] || {};
    return row.status === "answered" && !row.unknown;
  }
  const tokens = (qid) => {
    const row = answers[qid] || {};
    if (row.unknown || row.status === "unknown") return ["unknown"];
    if (row.value) return [String(row.value)];
    return (row.values || []).map(String);
  };
  if (rule.eq) return tokens(rule.eq.question).includes(rule.eq.value);
  if (rule.neq) return !tokens(rule.neq.question).includes(rule.neq.value);
  if (rule.in) return tokens(rule.in.question).some((t) => (rule.in.values || []).includes(t));
  if (rule.includes) return tokens(rule.includes.question).includes(rule.includes.value);
  return true;
}

function rowFrom(answers, qid) {
  return { ...EMPTY_ANSWER, ...(answers[qid] || {}), question_id: qid };
}

function setSingle(answers, qid, value, unknownKey) {
  const next = { ...answers };
  const unknown = value === unknownKey;
  next[qid] = {
    ...rowFrom(answers, qid),
    value,
    values: [],
    unknown,
    status: unknown ? "unknown" : "answered",
  };
  return next;
}

function toggleMulti(answers, qid, key, unknownKey, otherKey) {
  const row = rowFrom(answers, qid);
  let values = [...(row.values || [])];
  if (key === unknownKey) {
    values = values.includes(unknownKey) ? [] : [unknownKey];
  } else {
    values = values.filter((v) => v !== unknownKey);
    values = values.includes(key) ? values.filter((v) => v !== key) : [...values, key];
  }
  const unknown = values.length === 1 && values[0] === unknownKey;
  return {
    ...answers,
    [qid]: {
      ...row,
      values,
      value: "",
      unknown,
      status: unknown ? "unknown" : values.length ? "answered" : "empty",
      custom_request: key === otherKey && !values.includes(otherKey) ? "" : row.custom_request,
    },
  };
}

function setText(answers, qid, text) {
  return {
    ...answers,
    [qid]: {
      ...rowFrom(answers, qid),
      text,
      status: String(text || "").trim() ? "answered" : "empty",
    },
  };
}

function setCustom(answers, qid, text) {
  return {
    ...answers,
    [qid]: { ...rowFrom(answers, qid), custom_request: text },
  };
}

function canSeeExpert(user) {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  if (user.sales_level === "sales_super_admin") return true;
  // Existing calculator users keep access until STEP 13 RBAC.
  return user.role === "sub_admin" || user.department === "sales";
}

function QuestionBlock({ question, answers, unknownKey, otherKey, layout, onSingle, onMulti, onText, onCustom, onDate, onPeriod }) {
  const row = rowFrom(answers, question.id);
  const options = question.options || [];
  const isCore = layout === "purpose-cards" || layout === "core-cards" || layout === "cards" || question.answer_type === "single";
  const showOther = (row.values || []).includes(otherKey) || row.value === otherKey;

  return (
    <div className="sales-v2-q" data-testid={`sales-v2-q-${question.id}`}>
      <h4>{question.sales_label}</h4>
      {question.sales_description ? <p className="hint">{question.sales_description}</p> : null}

      {question.answer_type === "text" && (
        <textarea
          rows={3}
          value={row.text || ""}
          onChange={(e) => onText(question.id, e.target.value)}
          data-testid={`sales-v2-text-${question.id}`}
        />
      )}

      {question.answer_type === "date" && (
        <DateField
          value={row.date || ""}
          onChange={(v) => onDate(question.id, v)}
          testId={`sales-v2-date-${question.id}`}
        />
      )}

      {question.answer_type === "date_or_period" && (
        <>
          <div className="sales-v2-chips">
            {options.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={row.value === opt.key ? "is-on" : ""}
                data-testid={`sales-v2-option-${question.id}-${opt.key}`}
                onClick={() => onPeriod(question.id, opt.key)}
              >
                {opt.sales_label}
              </button>
            ))}
          </div>
          {row.value === "date" && (
            <DateField value={row.date || ""} onChange={(v) => onDate(question.id, v, "date")} testId={`sales-v2-date-${question.id}`} />
          )}
        </>
      )}

      {question.answer_type === "single" && isCore && layout !== "chips" && (
        <div className="sales-v2-grid">
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`sales-v2-choice${row.value === opt.key ? " is-on" : ""}`}
              data-testid={`sales-v2-option-${question.id}-${opt.key}`}
              onClick={() => onSingle(question.id, opt.key)}
            >
              <strong>{opt.sales_label}</strong>
              {opt.description ? <span>{opt.description}</span> : null}
            </button>
          ))}
        </div>
      )}

      {question.answer_type === "multi" && (
        <div className="sales-v2-chips">
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`${(row.values || []).includes(opt.key) ? "is-on" : ""}${opt.is_other ? " is-other" : ""}`}
              data-testid={`sales-v2-option-${question.id}-${opt.key}`}
              onClick={() => onMulti(question.id, opt.key)}
            >
              {opt.sales_label}
            </button>
          ))}
        </div>
      )}

      {(showOther || question.allow_custom_request) && question.answer_type === "multi" && showOther && (
        <textarea
          rows={2}
          placeholder="원하는 기능을 적어 주세요. 가격은 이 화면에서 정하지 않습니다."
          value={row.custom_request || ""}
          onChange={(e) => onCustom(question.id, e.target.value)}
          data-testid={`sales-v2-custom-${question.id}`}
        />
      )}
    </div>
  );
}

function won(n) {
  if (n == null || n === "") return "—";
  return `₩${Number(n).toLocaleString("ko-KR")}`;
}

function rangeText(min, max) {
  if (!min && !max) return null;
  if (min === max) return won(min);
  return `${won(min)} ~ ${won(max)}`;
}

function itemMoney(item) {
  if (item?.min == null && item?.max == null) return item?.note || "";
  return rangeText(item.min, item.max);
}

function ResultView({ result, customers, expertOk, converting, convertOpen, convertForm, onConvertForm, onOpenConvert, onCloseConvert, onSubmitConvert, onExpert, onBack, onReset, onRestart }) {
  const pkg = result.package_view || {};
  const review = result.review_ui || {};
  const estimate = result.estimate || {};
  const showEstimate = Boolean(estimate.show_range || estimate.presentation?.customer_quote?.rows?.length);
  const confirmBlocked = estimate.confirm_blocked || !result.final_quote_allowed;
  const notice = estimate.provisional_notice || pkg.convert_hint;
  const isFullCustom = result.route_type === "FULL_CUSTOM_REVIEW";
  const isPlatformReview = result.route_type === "PLATFORM_REVIEW";
  const gapItems = pkg.unpriced_gap_items || estimate.unpriced_gap_items || [];
  const convertBlockedReason = pkg.convert_blocked_reason || estimate.convert_blocked_reason || "";

  return (
    <div className="sales-v2-panel sales-v2-result" data-testid="sales-v2-result">
      <div className={`sales-v2-badge ${review.tone || "ok"}`} data-testid="sales-v2-review">
        {review.label || "검토 상태 확인"}
      </div>
      {result.route_label ? (
        <div className="sales-v2-badge info" data-testid="sales-v2-route">
          추천 유형 · {result.route_label}
        </div>
      ) : null}
      {confirmBlocked ? (
        <div className="sales-v2-banner" data-testid="sales-v2-final-block">
          예상 견적은 산출합니다. {review.label || "추가 검토"}가 필요해 지금은 최종 견적을 확정할 수 없습니다.
        </div>
      ) : null}
      <p className="meta" style={{ fontSize: 12, color: "rgba(255,255,255,.45)", marginTop: 10 }}>추천</p>
      <h3 data-testid="sales-v2-product">{result.recommended_product_label}</h3>
      <p className="summary">{result.summary}</p>

      {showEstimate ? (
        <div className="sales-v2-estimate" data-testid="sales-v2-estimate">
          <p className="meta">{isFullCustom ? "현재 확인된 기능 기준 예상 범위" : isPlatformReview ? "플랫폼 구조 검토 전 예상 범위" : "영업 예상 견적"}</p>
          {estimate.show_range ? (
            <>
              <div className="sales-v2-estimate__range" data-testid="sales-v2-range">
                {rangeText(estimate.range_min, estimate.range_max)}
              </div>
              <p className="hint" data-testid="sales-v2-range-note">{estimate.package_policy?.range_note || "현재 확인된 기능 기준 예상 범위"}</p>
              {estimate.sales_offer_price ? (
                <div className="sales-v2-estimate__offer" data-testid="sales-v2-offer">
                  영업 안내 기준가 {won(estimate.sales_offer_price)}
                </div>
              ) : null}
            </>
          ) : (
            <div className="sales-v2-estimate__consult" data-testid="sales-v2-consult-only">별도 상담</div>
          )}
          {!estimate.range_reliable && estimate.show_range ? (
            <p className="hint" data-testid="sales-v2-range-caveat">
              {isPlatformReview
                ? "현재 확인된 기능 기준 예상 범위입니다. 플랫폼 구조 및 기술 검토 후 최종 견적이 확정됩니다."
                : isFullCustom
                  ? "현재 표시 금액은 미산정 항목을 제외한 임시 범위입니다."
                  : "현재 확인된 기능 기준 예상 범위이며, 최종 금액은 기술 검토 후 달라질 수 있습니다."}
            </p>
          ) : null}
          {(estimate.package_policy?.custom_review_items || []).length ? (
            <p className="hint" data-testid="sales-v2-custom-items">추가 기술검토 필요: {(estimate.package_policy.custom_review_items).join(", ")}</p>
          ) : null}
          {(estimate.notes || []).map((note) => (
            <p key={note} className="sales-v2-estimate__note">{note}</p>
          ))}
        </div>
      ) : (
        <div className="sales-v2-price" data-testid="sales-v2-price-note">{pkg.price_note || result.price_note || "별도 상담"}</div>
      )}

      <div className="sales-v2-notice" data-testid="sales-v2-provisional-notice">{notice}</div>

      {(() => {
        const pres = estimate.presentation || {};
        const view = pres.sales_view || {};
        const quote = pres.customer_quote || {};
        const quoteRows = quote.rows || [];
        return (
          <>
            {(view.basic || []).length ? (
              <div className="sales-v2-block" data-testid="sales-v2-basic">
                <h4>BASIC 포함</h4>
                <ul>
                  {view.basic.map((g) => (
                    <li key={g.sales_label}>
                      <strong>{g.sales_label}</strong>
                      {g.display_items?.length ? ` — ${(g.display_items || []).slice(0, 6).join(", ")}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {(view.plus || []).length ? (
              <div className="sales-v2-block" data-testid="sales-v2-plus">
                <h4>PLUS</h4>
                <ul>{view.plus.map((g) => <li key={g.sales_label}>{g.sales_label}</li>)}</ul>
              </div>
            ) : null}
            {(view.add_on || []).length ? (
              <div className="sales-v2-block" data-testid="sales-v2-addon">
                <h4>추가 기능</h4>
                <ul>{view.add_on.map((g) => <li key={g.sales_label}>{g.sales_label}</li>)}</ul>
              </div>
            ) : null}
            {(view.tech_review || []).length || (estimate.package_policy?.custom_review_items || []).length || (pkg.tech_review_items || []).length ? (
              <div className="sales-v2-block" data-testid="sales-v2-tech">
                <h4>기술 검토 필요</h4>
                <ul>
                  {(view.tech_review || []).map((g) => <li key={g.sales_label}>{g.sales_label}{g.note ? ` — ${g.note}` : ""}</li>)}
                  {(estimate.package_policy?.custom_review_items || []).map((item) => <li key={item}>{item}</li>)}
                  {(pkg.tech_review_items || []).map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
            ) : null}
            {gapItems.length ? (
              <div className="sales-v2-block" data-testid="sales-v2-unpriced-gaps">
                <h4>가격 미산정 / 기술 검토 필요</h4>
                <ul>
                  {gapItems.map((item) => <li key={item}>{item}</li>)}
                </ul>
                <p className="hint">현재 표시 금액은 위 미산정 항목을 제외한 임시 범위입니다. 미산정 항목이 무료인 것은 아닙니다.</p>
              </div>
            ) : null}
            <div className="sales-v2-block" data-testid="sales-v2-auto">
              <h4>자동 기술 구성</h4>
              <p className="hint">{view.auto_note || pres.auto_note || "서버 및 데이터 저장 구성은 선택한 기능에 따라 자동 구성됩니다."}</p>
              {view.third_party_note || pres.third_party_note ? (
                <p className="hint">{view.third_party_note || pres.third_party_note}</p>
              ) : null}
            </div>
            {quoteRows.length ? (
              <div className="sales-v2-preview" data-testid="sales-v2-customer-preview">
                <h4>고객 견적 미리보기</h4>
                {quoteRows.map((row) => (
                  <div key={row.label} className="sales-v2-preview__row">
                    <div>
                      <strong>{row.label}</strong>
                      {row.display_items?.length ? <p className="hint">포함: {row.display_items.join(" · ")}</p> : null}
                    </div>
                    <b>{won(row.amount)}</b>
                  </div>
                ))}
                <div className="sales-v2-preview__sum"><span>공급가</span><b>{won(quote.supply_amount)}</b></div>
                <div className="sales-v2-preview__sum"><span>VAT</span><b>{won(quote.vat_amount)}</b></div>
                <div className="sales-v2-preview__sum is-total"><span>총액</span><b>{won(quote.total_with_vat)}</b></div>
                {gapItems.length ? (
                  <div className="sales-v2-preview__gaps" data-testid="sales-v2-customer-gaps">
                    <h5>기술 검토 필요 / 미산정 항목</h5>
                    <ul>
                      {gapItems.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                    <p className="hint">현재 금액에는 위 미산정 항목이 포함되지 않습니다.</p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        );
      })()}

      <div className="sales-v2-block" data-testid="sales-v2-reasons">
        <h4>왜 이 구성을 추천했나요?</h4>
        <ul>
          {(result.recommendation_reasons || []).map((item) => <li key={item}>✓ {item}</li>)}
        </ul>
      </div>

      {(pkg.confirm_needed?.items || []).length > 0 && (
        <div className="sales-v2-block" data-testid="sales-v2-confirm-needed">
          <h4>{pkg.confirm_needed.title}</h4>
          <ul>
            {pkg.confirm_needed.items.map((item) => <li key={item}>? {item}</li>)}
          </ul>
        </div>
      )}

      <details className="sales-v2-advanced" data-testid="sales-v2-advanced">
        <summary>고급 정보 보기</summary>
        {pkg.custom_review_possible ? (
          <div className="sales-v2-block" data-testid="sales-v2-custom-review">
            <h4>목록에 없는 기능</h4>
            <ul><li>목록에 없는 기능은 기존 단가로 금액을 만들지 않고 별도 상담합니다.</li></ul>
          </div>
        ) : null}
        {(pkg.alternatives || []).length > 0 && (
          <div className="sales-v2-block" data-testid="sales-v2-alts">
            <h4>다른 가능성</h4>
            <ul>
              {pkg.alternatives.map((alt) => (
                <li key={alt.product}>{alt.label} — {alt.reason}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="hint">내부 산출 코드는 고객 견적서에 표시되지 않습니다.</p>
      </details>

      {convertBlockedReason && !pkg.convert_enabled ? (
        <div className="sales-v2-banner" data-testid="sales-v2-convert-blocked">
          {convertBlockedReason}
        </div>
      ) : null}

      <div className="sales-v2-nav">
        <button type="button" className="sales-v2-btn ghost" onClick={onBack} data-testid="sales-v2-back">뒤로</button>
        <button type="button" className="sales-v2-btn ghost" onClick={onReset} data-testid="sales-v2-reset">처음부터 다시</button>
        <button
          type="button"
          className="sales-v2-btn ghost"
          onClick={onExpert}
          disabled={!expertOk || !(estimate.prefill_lines || []).length}
          data-testid="sales-v2-to-expert"
        >
          전문가 계산기로 조정
        </button>
        <button
          type="button"
          className="sales-v2-btn"
          disabled={!pkg.convert_enabled || converting}
          data-testid="sales-v2-convert"
          title={pkg.convert_hint}
          onClick={onOpenConvert}
        >
          {converting ? "저장 중..." : (pkg.convert_label || "임시 견적으로 저장")}
        </button>
        <button type="button" className="sales-v2-btn ghost" onClick={onRestart}>첫 화면</button>
      </div>

      {convertOpen && (
        <div className="sales-v2-modal" data-testid="sales-v2-convert-modal">
          <form className="sales-v2-modal__card" onSubmit={onSubmitConvert}>
            <h4>임시 견적으로 저장</h4>
            <p className="sales-v2-notice">{notice}</p>
            {confirmBlocked ? <p className="sales-v2-estimate__note">최종 확정은 불가합니다. 상담용 임시 견적만 저장됩니다.</p> : null}
            <input
              placeholder="견적 제목"
              value={convertForm.title}
              onChange={(e) => onConvertForm({ ...convertForm, title: e.target.value })}
              data-testid="sales-v2-convert-title"
            />
            <select
              value={convertForm.customer_id}
              onChange={(e) => onConvertForm({ ...convertForm, customer_id: e.target.value })}
              data-testid="sales-v2-convert-customer"
            >
              <option value="">고객 선택 (선택)</option>
              {(customers || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.email || c.id}</option>
              ))}
            </select>
            <input
              placeholder="고객 이메일 (선택)"
              value={convertForm.customer_email}
              onChange={(e) => onConvertForm({ ...convertForm, customer_email: e.target.value })}
            />
            <textarea
              rows={3}
              placeholder="내부 메모 (선택)"
              value={convertForm.notes}
              onChange={(e) => onConvertForm({ ...convertForm, notes: e.target.value })}
            />
            <div className="sales-v2-nav">
              <button type="button" className="sales-v2-btn ghost" onClick={onCloseConvert}>취소</button>
              <button type="submit" className="sales-v2-btn" disabled={converting} data-testid="sales-v2-convert-submit">
                {converting ? "저장 중..." : (pkg.convert_label || "임시 견적 저장")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default function SalesCalculatorV2({ customers = [], onConverted }) {
  const { user } = useAuth();
  const expertOk = canSeeExpert(user);
  const [mode, setMode] = useState("entry");
  const [wizardFlow, setWizardFlow] = useState("recommend");
  const [pack, setPack] = useState(null);
  const [packError, setPackError] = useState("");
  const [answers, setAnswers] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);
  const [expertPrefill, setExpertPrefill] = useState(null);
  const [expertKey, setExpertKey] = useState(0);
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertForm, setConvertForm] = useState({ title: "", customer_id: "", customer_email: "", notes: "" });
  const convertingRef = useRef(false);
  const [pendingPrefill, setPendingPrefill] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/sales-v2/questions");
        if (alive) setPack(r.data);
      } catch {
        if (alive) setPackError("추천 질문 정보를 불러오지 못했습니다.");
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!pack) return;
    const readPrefill = () => {
      try {
        const raw = sessionStorage.getItem("dsite_v2_prefill");
        if (!raw) return;
        sessionStorage.removeItem("dsite_v2_prefill");
        setPendingPrefill(JSON.parse(raw));
      } catch {
        /* ignore broken prefill */
      }
    };
    readPrefill();
    window.addEventListener("dsite-v2-prefill", readPrefill);
    return () => window.removeEventListener("dsite-v2-prefill", readPrefill);
  }, [pack]);

  useEffect(() => {
    if (!pack || !pendingPrefill) return;
    const wizard = pendingPrefill.wizard_flow === "custom" ? "custom" : "recommend";
    const blob = wizard === "custom"
      ? (pendingPrefill.wizard_answers || pendingPrefill.canonical_answers || {})
      : (pendingPrefill.canonical_answers || pendingPrefill.wizard_answers || {});
    const prefill = pendingPrefill;
    setPendingPrefill(null);
    setWizardFlow(wizard);
    setAnswers(blob);
    setLoadingRec(true);
    api.post("/admin/sales-v2/recommend", {
      flow: wizard === "custom" ? "custom" : undefined,
      answers: blob,
    }).then((r) => {
      setResult(r.data);
      setMode("result");
      toast.success("고객 요구사항을 계산기에 불러왔습니다.");
    }).catch(() => {
      setMode(wizard);
      toast.success("고객 요구사항을 계산기에 불러왔습니다. 추천은 다음 단계에서 실행하세요.");
    }).finally(() => {
      setLoadingRec(false);
    });
    return undefined;
  }, [pack, pendingPrefill]);

  const unknownKey = pack?.unknown_key || "unknown";
  const otherKey = pack?.other_key || "other";
  const steps = wizardFlow === "custom" ? (pack?.custom_steps || []) : (pack?.steps || []);

  const visibleSteps = useMemo(() => {
    return steps
      .map((step, index) => {
        const stepVisible = evalShowWhen(step.show_when, answers);
        const questions = (step.questions || []).filter((q) => evalShowWhen(q.show_when, answers));
        return { ...step, index, visible: stepVisible && questions.length > 0, questions };
      })
      .filter((s) => s.visible);
  }, [steps, answers]);

  const current = visibleSteps[Math.min(stepIndex, Math.max(visibleSteps.length - 1, 0))];

  const goRecommend = async (payload, flowOverride) => {
    setLoadingRec(true);
    try {
      const flow = flowOverride || wizardFlow;
      const r = await api.post("/admin/sales-v2/recommend", {
        flow: flow === "custom" ? "custom" : undefined,
        answers: payload || answers,
      });
      setResult(r.data);
      setMode("result");
    } catch {
      toast.error("추천 결과를 만들지 못했습니다.");
    } finally {
      setLoadingRec(false);
    }
  };

  const next = async () => {
    let nextAnswers = answers;
    if (current?.id === "purpose") {
      const purpose = (answers.purpose || {}).value;
      if (purpose === "landing" && !(answers.page_scale || {}).value) {
        nextAnswers = setSingle(answers, "page_scale", "one_page", unknownKey);
        setAnswers(nextAnswers);
      }
    }
    const nextIndex = stepIndex + 1;
    if (nextIndex >= visibleSteps.length) {
      await goRecommend(nextAnswers);
      return;
    }
    setStepIndex(nextIndex);
  };

  const back = () => {
    if (mode === "result") {
      setMode(wizardFlow === "custom" ? "custom" : "recommend");
      setStepIndex(Math.max(visibleSteps.length - 1, 0));
      return;
    }
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const resetLocal = () => {
    setAnswers({});
    setStepIndex(0);
    setResult(null);
    setMode(wizardFlow === "custom" ? "custom" : "recommend");
  };

  const openExpertFromResult = () => {
    const lines = result?.estimate?.prefill_lines || [];
    if (!expertOk || !lines.length) {
      toast.error("전문가 계산기로 넘길 선택 항목이 없습니다.");
      return;
    }
    try {
      sessionStorage.setItem("dsite_calc_prefill", JSON.stringify({
        lines,
        custom_needed: result?.unpriced_gap_items || result?.estimate?.unpriced_gap_items || [],
        tech_review_items: result?.tech_review_items || [],
        unpriced_gap_items: result?.unpriced_gap_items || result?.estimate?.unpriced_gap_items || [],
        route_type: result?.route_type || "",
        confirmed_scope_only: Boolean(result?.estimate?.confirmed_scope_only || result?.package_view?.confirmed_scope_only),
        estimate_scope: result?.estimate?.estimate_scope || result?.package_view?.estimate_scope || "",
        price_confidence: result?.price_confidence || "",
        display_intent: result?.display_intent || "",
        recommended_product: result?.recommended_product || "",
      }));
    } catch {
      /* ignore */
    }
    setExpertPrefill(lines);
    setExpertKey((n) => n + 1);
    setMode("expert");
  };

  const openConvert = () => {
    if (!result?.package_view?.convert_enabled) return;
    setConvertForm({
      title: convertForm.title || `${result.recommended_product_label || "웹사이트"} 임시 견적`,
      customer_id: convertForm.customer_id,
      customer_email: convertForm.customer_email,
      notes: convertForm.notes,
    });
    setConvertOpen(true);
  };

  const submitConvert = async (e) => {
    e.preventDefault();
    if (convertingRef.current) return;
    if (!convertForm.title.trim()) return toast.error("견적 제목이 필요합니다.");
    const lines = result?.estimate?.prefill_lines || [];
    if (!lines.length) return toast.error("저장할 예상 견적 항목이 없습니다.");
    convertingRef.current = true;
    setConverting(true);
    try {
      const notice = result?.estimate?.provisional_notice || "";
      const extra = convertForm.notes?.trim();
      const r = await api.post("/admin/sales-calculator/convert", {
        title: convertForm.title,
        customer_id: convertForm.customer_id || undefined,
        customer_email: convertForm.customer_email || undefined,
        notes: extra ? `${notice}\n${extra}` : notice,
        sales_offer_price: result?.estimate?.sales_offer_price || result?.estimate?.confirmed_offer_price || undefined,
        recommended_product: result?.recommended_product || undefined,
        package_mode: result?.estimate?.package_policy?.package_mode || undefined,
        display_intent: result?.display_intent || undefined,
        route_type: result?.route_type || undefined,
        price_confidence: result?.price_confidence || undefined,
        tech_review_items: result?.tech_review_items || undefined,
        unpriced_gap_items: result?.unpriced_gap_items || undefined,
        confirmed_scope_only: Boolean(result?.estimate?.confirmed_scope_only) || undefined,
        estimate_scope: result?.estimate?.estimate_scope || undefined,
        lines: lines.map((l) => ({ master_id: l.master_id, quantity: l.quantity || 1 })),
      });
      toast.success("임시 견적이 저장되었습니다. 견적 관리에서 확인하세요.");
      setConvertOpen(false);
      refreshAdminNotifications();
      onConverted?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "임시 견적을 저장하지 못했습니다.");
    } finally {
      convertingRef.current = false;
      setConverting(false);
    }
  };

  if (mode === "expert") {
    return (
      <div className="sales-v2" data-testid="sales-v2-expert">
        <div className="sales-v2-expert-bar">
          <div>
            <h3>전문가 계산기 (기존)</h3>
            <p>추천 결과의 선택 항목을 가져와 세부 단가를 조정합니다.</p>
          </div>
          <button type="button" className="sales-v2-btn ghost" onClick={() => setMode(result ? "result" : "entry")} data-testid="sales-v2-back-entry">
            {result ? "추천 결과로" : "V2 첫 화면"}
          </button>
        </div>
        <SalesCalculator key={expertKey} customers={customers} onConverted={onConverted} prefillLines={expertPrefill} />
      </div>
    );
  }

  return (
    <div className="sales-v2" data-testid="sales-v2">
      <AdminPageHeader
        title="영업 견적 계산기"
        description="고객이 원하는 내용을 고르면 적합한 홈페이지 유형을 추천합니다. 기존 전문가 계산기도 그대로 사용할 수 있습니다."
        testId="sales-v2-header"
        actions={(
          <button
            type="button"
            className="sales-v2-btn ghost"
            data-testid="sales-v2-prefill"
            onClick={() => {
              try {
                const raw = sessionStorage.getItem("dsite_v2_prefill");
                if (!raw) {
                  toast.error("불러올 상담 내용이 없습니다. 현장 상담에서 [견적 계산기로 불러오기]를 눌러 주세요.");
                  return;
                }
                sessionStorage.removeItem("dsite_v2_prefill");
                setPendingPrefill(JSON.parse(raw));
              } catch {
                toast.error("상담 내용을 불러오지 못했습니다.");
              }
            }}
          >
            기존 상담 내용 불러오기
          </button>
        )}
      />

      {mode === "entry" && (
        <div className="sales-v2__entry" data-testid="sales-v2-entry">
          <div className="sales-v2-card is-primary">
            <p className="meta">기본</p>
            <h3>추천 견적</h3>
            <p>고객이 원하는 내용을 선택하면 적합한 홈페이지 유형과 필요한 기능을 추천합니다.</p>
            <button type="button" className="sales-v2-btn" data-testid="sales-v2-start-recommend" onClick={() => { setWizardFlow("recommend"); setMode("recommend"); setStepIndex(0); setAnswers({}); }}>
              추천 견적 시작
            </button>
          </div>
          <div className="sales-v2-card is-secondary">
            <p className="meta">복잡한 프로젝트</p>
            <h3>맞춤형 개발 견적</h3>
            <p>업무시스템·플랫폼·연동처럼 일반 홈페이지보다 복잡한 요구를 정리합니다.</p>
            <button type="button" className="sales-v2-btn ghost" data-testid="sales-v2-start-custom" onClick={() => { setWizardFlow("custom"); setMode("custom"); setStepIndex(0); setAnswers({}); }}>
              맞춤형 상담 시작
            </button>
          </div>
          <div className={`sales-v2-card is-advanced${expertOk ? "" : " is-disabled"}`}>
            <p className="meta">고급 · 내부용</p>
            <h3>전문가 계산기</h3>
            <p>세부 단가를 직접 조정하는 화면입니다. 일반 견적 작성에는 사용하지 않습니다.</p>
            <button
              type="button"
              className={expertOk ? "sales-v2-btn ghost" : "sales-v2-btn is-soon"}
              disabled={!expertOk}
              data-testid="sales-v2-start-expert"
              onClick={() => expertOk && setMode("expert")}
            >
              {expertOk ? "전문가 계산기 열기" : "권한 없음"}
            </button>
          </div>
        </div>
      )}

      {(mode === "recommend" || mode === "custom") && (
        <div data-testid={mode === "custom" ? "sales-v2-custom-wizard" : "sales-v2-wizard"}>
          {packError && <div className="sales-v2-panel">{packError}</div>}
          <div className="sales-v2-progress" data-testid="sales-v2-progress">
            {steps.map((step, i) => {
              const vis = visibleSteps.find((s) => s.id === step.id);
              const active = current?.id === step.id;
              const done = vis && visibleSteps.findIndex((s) => s.id === step.id) < stepIndex;
              return (
                <button
                  key={step.id}
                  type="button"
                  className={`${active ? "is-on" : ""}${done ? " is-done" : ""}`}
                  data-testid={`sales-v2-step-${step.id}`}
                  onClick={() => vis && setStepIndex(visibleSteps.findIndex((s) => s.id === step.id))}
                >
                  {i + 1} {step.short}
                </button>
              );
            })}
            <button type="button" className="is-done" disabled>추천</button>
          </div>

          <div className="sales-v2-panel">
            <h3 style={{ fontSize: 16, marginBottom: 12 }}>{current?.title}</h3>
            {(current?.questions || []).map((q) => (
              <QuestionBlock
                key={q.id}
                question={q}
                answers={answers}
                unknownKey={unknownKey}
                otherKey={otherKey}
                layout={current.layout}
                onSingle={(id, key) => setAnswers((prev) => setSingle(prev, id, key, unknownKey))}
                onMulti={(id, key) => setAnswers((prev) => toggleMulti(prev, id, key, unknownKey, otherKey))}
                onText={(id, text) => setAnswers((prev) => setText(prev, id, text))}
                onCustom={(id, text) => setAnswers((prev) => setCustom(prev, id, text))}
                onDate={(id, date, period) => setAnswers((prev) => ({
                  ...prev,
                  [id]: {
                    ...rowFrom(prev, id),
                    date,
                    value: period || rowFrom(prev, id).value || "date",
                    status: date || period ? "answered" : "empty",
                  },
                }))}
                onPeriod={(id, key) => setAnswers((prev) => setSingle(prev, id, key, unknownKey))}
              />
            ))}
            <div className="sales-v2-nav">
              <button type="button" className="sales-v2-btn ghost" onClick={() => (stepIndex === 0 ? setMode("entry") : back())} data-testid="sales-v2-back">
                뒤로
              </button>
              <button type="button" className="sales-v2-btn ghost" onClick={resetLocal} data-testid="sales-v2-reset">처음부터 다시</button>
              <button type="button" className="sales-v2-btn" onClick={next} disabled={loadingRec || !pack} data-testid="sales-v2-next">
                {stepIndex >= visibleSteps.length - 1 ? (loadingRec ? "추천 중..." : "추천 보기") : "다음"}
              </button>
            </div>
          </div>
        </div>
      )}

      {mode === "result" && result && (
        <ResultView
          result={result}
          customers={customers}
          expertOk={expertOk}
          converting={converting}
          convertOpen={convertOpen}
          convertForm={convertForm}
          onConvertForm={setConvertForm}
          onOpenConvert={openConvert}
          onCloseConvert={() => setConvertOpen(false)}
          onSubmitConvert={submitConvert}
          onExpert={openExpertFromResult}
          onBack={back}
          onReset={resetLocal}
          onRestart={() => { setMode("entry"); setResult(null); setConvertOpen(false); }}
        />
      )}
    </div>
  );
}
